import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { stratum } from '../../constants/stratum.js';

/**
 * Calculates app counts from the provided apps array
 * @param {Array} apps - Array of application objects
 * @returns {Object} App counts object with totalAppsCount, activeAppsCount, hasMultipleApps
 */
export function calculateAppCounts(apps) {
  // Active app statuses based on new hierarchy (Tiers 1-4)
  const ACTIVE_STATUSES = ['Funding', 'Approved', 'Reviewing', 'Verifying', 'Received', 'Pending', 'Funded'];

  // Filter active apps (active status + not expired)
  const activeApps = apps.filter(app => {
    const hasActiveStatus = ACTIVE_STATUSES.includes(app?.applicationStatus);
    const isNotExpired = !app?.expirationTimestamp || new Date(app.expirationTimestamp) > new Date();
    return hasActiveStatus && isNotExpired;
  });

  return {
    totalAppsCount: apps.length,
    activeAppsCount: activeApps.length,
    hasMultipleApps: apps.length > 1,
  };
}

/**
 * Service for managing and publishing app counts analytics across pick-an-app components
 * Maintains state to prevent duplicate publications and provides consistent analytics tracking
 */
class _AppCountsAnalyticsService {
  previousAppCounts = null;

  /**
   * Publishes app counts analytics if they have changed since last publication
   * Uses guard logic to prevent duplicate events for the same data
   * @param {Array} apps - Array of application objects
   */
  publishAppCounts(apps) {
    // Guard: Validate input
    if (!apps || !Array.isArray(apps)) {
      return;
    }

    const appCounts = calculateAppCounts(apps);

    // Guard: Only publish if counts have changed
    const hasChanged = !this.previousAppCounts || JSON.stringify(this.previousAppCounts) !== JSON.stringify(appCounts);

    if (hasChanged) {
      stratum.publishTag(TagKey.PICK_AN_APP_V2_APPS_COUNT, {
        replacements: { value: JSON.stringify(appCounts) },
      });

      // Store current counts for next comparison
      this.previousAppCounts = { ...appCounts };
    }
  }

  /**
   * Resets the service state, clearing previous app counts
   * Useful for testing or component reinitialization
   */
  reset() {
    this.previousAppCounts = null;
  }

  /**
   * Gets the current app counts without publishing
   * @param {Array} apps - Array of application objects
   * @returns {Object} Current app counts
   */
  getCurrentCounts(apps) {
    return calculateAppCounts(apps);
  }

  /**
   * Checks if the service has published any analytics yet
   * @returns {boolean} True if analytics have been published before
   */
  hasPublishedBefore() {
    return this.previousAppCounts !== null;
  }
}

export const AppCountsAnalyticsService = new _AppCountsAnalyticsService();
